// @ts-nocheck
import axios from "axios";
import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowPathIcon,
  EnvelopeIcon,
  UserIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { URL } from "../../service/Service";
import { useTranslation } from "../../i18n/LanguageContext";

const CreateAdminModal = ({ show, handleClose, userRole, refreshAdmins }) => {
  const { t } = useTranslation();
  const normalizedUserRole = String(userRole || "MANAGER").toUpperCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(normalizedUserRole === "ADMIN" ? "ADMIN" : "MANAGER");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (show) {
      setRole(normalizedUserRole === "ADMIN" ? "ADMIN" : "MANAGER");
    }
  }, [show, normalizedUserRole]);

  const clearForm = () => {
    setName("");
    setEmail("");
    setRole(normalizedUserRole === "ADMIN" ? "ADMIN" : "MANAGER");
    setErrors({});
    setSuccessMessage("");
  };

  const closeModal = () => {
    if (isSaving) return;
    handleClose();
    clearForm();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = t("admins.create.nameRequired");
    if (!email.trim()) newErrors.email = t("admins.create.emailRequired");
    else if (!/^\S+@\S+\.\S+$/.test(email.trim())) newErrors.email = t("admins.create.emailInvalid");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    if (!validateForm() || isSaving) return;

    setIsSaving(true);
    setErrors({});
    setSuccessMessage("");

    try {
      const jwtToken = sessionStorage.getItem("jwt");
      const endpoint = role === "ADMIN" ? "/admin/create" : "/manager/create";
      await axios.post(
        `${URL}/api${endpoint}`,
        { name: name.trim(), email: email.trim(), role },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );

      setSuccessMessage(t("admins.create.success"));
      await refreshAdmins();
      setTimeout(() => closeModal(), 900);
    } catch (error) {
      const backendMessage = error?.response?.data?.message || error?.response?.data;
      const message = typeof backendMessage === "string" ? backendMessage : t("admins.create.error");
      if (message.toLowerCase().includes("email") && message.toLowerCase().includes("use")) {
        setErrors({ email: t("admins.create.emailUsed") });
      } else {
        setErrors({ form: message });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const fieldClass = (hasError) =>
    `block min-h-[48px] w-full rounded-xl border bg-white pl-11 pr-4 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
      hasError
        ? "border-red-300 focus:border-red-400 focus:ring-red-100"
        : "border-gray-200 focus:border-green-cl focus:ring-green-100"
    }`;

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-950/45 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-3 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-3 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl overflow-hidden rounded-3xl border border-white/70 bg-white text-left shadow-2xl">
                <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 sm:px-8 sm:py-6">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-green-50 text-green-cl">
                      <UserPlusIcon className="h-6 w-6" />
                    </span>
                    <div>
                      <Dialog.Title as="h3" className="text-xl font-extrabold text-gray-900">
                        {t("admins.create.title")}
                      </Dialog.Title>
                      <p className="mt-1 text-sm leading-5 text-gray-500">{t("admins.create.subtitle")}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={isSaving}
                    className="ml-4 rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-cl"
                    aria-label={t("common.close")}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSave} className="space-y-5 px-6 py-6 sm:px-8">
                  {errors.form && (
                    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {errors.form}
                    </div>
                  )}
                  {successMessage && (
                    <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                      {successMessage}
                    </div>
                  )}

                  <div>
                    <label htmlFor="create-admin-name" className="mb-2 block text-sm font-semibold text-gray-700">
                      {t("settings.name")}
                    </label>
                    <div className="relative">
                      <UserIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        id="create-admin-name"
                        type="text"
                        autoComplete="name"
                        className={fieldClass(errors.name)}
                        placeholder={t("admins.create.namePlaceholder")}
                        value={name}
                        onChange={(e) => { setName(e.target.value); setErrors((current) => ({ ...current, name: undefined })); }}
                        aria-invalid={Boolean(errors.name)}
                      />
                    </div>
                    {errors.name && <p className="mt-1.5 text-xs font-medium text-red-600">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="create-admin-email" className="mb-2 block text-sm font-semibold text-gray-700">Email</label>
                    <div className="relative">
                      <EnvelopeIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        id="create-admin-email"
                        type="email"
                        autoComplete="email"
                        className={fieldClass(errors.email)}
                        placeholder={t("admins.create.emailPlaceholder")}
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setErrors((current) => ({ ...current, email: undefined })); }}
                        aria-invalid={Boolean(errors.email)}
                      />
                    </div>
                    {errors.email && <p className="mt-1.5 text-xs font-medium text-red-600">{errors.email}</p>}
                  </div>

                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm leading-5 text-emerald-800">
                    {t("admins.create.passwordSetupInfo")}
                  </div>

                  <div>
                    <label htmlFor="create-admin-role" className="mb-2 block text-sm font-semibold text-gray-700">
                      {t("admins.accountType")}
                    </label>
                    {normalizedUserRole === "ADMIN" ? (
                      <select
                        id="create-admin-role"
                        className="block min-h-[48px] w-full rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-900 shadow-sm focus:border-green-cl focus:outline-none focus:ring-2 focus:ring-green-100"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                      >
                        <option value="ADMIN">{t("role.admin")}</option>
                        <option value="MANAGER">{t("role.manager")}</option>
                      </select>
                    ) : (
                      <div className="flex min-h-[48px] items-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-medium text-gray-700">
                        {t("role.manager")}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={closeModal}
                      disabled={isSaving}
                      className="inline-flex min-h-[46px] items-center justify-center rounded-xl border border-gray-200 bg-white px-5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-green-cl px-5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-green-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
                    >
                      {isSaving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                      {isSaving ? t("admins.create.saving") : t("common.save")}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CreateAdminModal;
